import { useMemo } from "react";
import { WarningOutlined } from "@ant-design/icons";
import { Tag, Tooltip } from "antd";
import { useTranslation } from "react-i18next";

import { type ProductWithStats } from "@fitness-app/app-store";
import { type DiscountStripeSyncErrorCode } from "@fitness-app/data-models/entities/Discount";
import { PaymentClient, type PaymentClientEnum } from "@fitness-app/data-models/entities/Product";

interface OwnProps {
  productIds: string[];
  products: ProductWithStats[];
  isStripeSynced?: boolean;
  stripeSyncError?: string | null;
  stripeSyncErrorCode?: DiscountStripeSyncErrorCode | null;
  stripeSyncErrorParams?: Record<string, string> | null;
}

const gatewayTagColor: Partial<Record<PaymentClientEnum, string>> = {
  [PaymentClient.STRIPE]: "purple",
  [PaymentClient.TPAY]: "blue",
  [PaymentClient.PAY_NOW]: "geekblue",
  [PaymentClient.PAY_PO]: "cyan",
  [PaymentClient.MANUAL]: "default",
};

// fitness model: prices are Stripe-only; non-Stripe gateways (Tpay, ...) are enabled
// at the product level via `enabledPaymentGateways`. Coverage is therefore derived
// from the selected products' enabled gateways (default: Stripe when not set).
const collectGatewaysForProduct = (product: ProductWithStats): PaymentClientEnum[] => {
  if (product.enabledPaymentGateways && product.enabledPaymentGateways.length > 0) {
    return product.enabledPaymentGateways;
  }

  return [PaymentClient.STRIPE];
};

const DiscountGatewayCoverage = ({
  productIds,
  products,
  isStripeSynced = true,
  stripeSyncError,
  stripeSyncErrorCode,
  stripeSyncErrorParams,
}: OwnProps) => {
  const { t } = useTranslation(["common", "settings"]);

  const gateways = useMemo(() => {
    const scoped = products.filter((product) => productIds.includes(product.id));

    const set = new Set<PaymentClientEnum>();
    scoped.forEach((product) => collectGatewaysForProduct(product).forEach((gateway) => set.add(gateway)));

    if (!isStripeSynced) {
      set.delete(PaymentClient.STRIPE);
    }

    return Array.from(set);
  }, [productIds, products, isStripeSynced]);

  const translatedSyncError = useMemo(() => {
    if (isStripeSynced) return null;
    if (stripeSyncErrorCode) {
      const params = { ...(stripeSyncErrorParams ?? {}) };
      if (params.existingType) {
        params.existingType = t(`settings:discount.stripeSync.couponType.${params.existingType}`);
      }
      if (params.requestedType) {
        params.requestedType = t(`settings:discount.stripeSync.couponType.${params.requestedType}`);
      }
      if (params.reason) {
        params.reason = t(`settings:discount.stripeSync.unusableReason.${params.reason}`);
      }

      return t(`settings:discount.stripeSync.errors.${stripeSyncErrorCode}`, params);
    }

    return stripeSyncError ?? null;
  }, [isStripeSynced, stripeSyncErrorCode, stripeSyncErrorParams, stripeSyncError, t]);

  const showSyncWarning = !isStripeSynced && !!translatedSyncError;

  if (gateways.length === 0 && !showSyncWarning) {
    return <span className="text-gray-400">—</span>;
  }

  return (
    <div className="flex items-center gap-2">
      {gateways.map((gateway) => (
        <Tag key={gateway} color={gatewayTagColor[gateway] ?? "default"}>
          {t(`common:paymentClient.${gateway.toLowerCase()}`, gateway)}
        </Tag>
      ))}
      {showSyncWarning && (
        <Tooltip title={translatedSyncError}>
          <WarningOutlined className="text-orange-500" />
        </Tooltip>
      )}
    </div>
  );
};

export default DiscountGatewayCoverage;
